import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { digitooApi } from '@/api/endpoints'
import type { DigitooSendRequest, DigitooSendResult } from '@/api/types'
import { useAuthStore } from '@/stores/useAuthStore'
import { errorMessage } from '@/utils/errors'
import { formatBytes } from '@/utils/format'
import { Button, Modal } from './ui'

/**
 * Potvrzení exportu do Digitoo. Nejdřív se zeptá backendu nanečisto
 * (dryRun), kolik dokladů odejde a co se přeskočí, a teprve po potvrzení
 * je zařadí do fronty.
 */
export default function DigitooExportDialog({
  selection,
  onClose,
  onQueued,
}: {
  selection: Pick<DigitooSendRequest, 'ids' | 'filter'>
  onClose: () => void
  onQueued: (result: DigitooSendResult) => void
}) {
  const isAdmin = useAuthStore((s) => s.user?.isAdmin)
  const [resend, setResend] = useState(false)
  const [preview, setPreview] = useState<DigitooSendResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setPreview(null)
    digitooApi
      .send({ ...selection, resend, dryRun: true })
      .then(setPreview)
      .catch((e) => setError(errorMessage(e, 'Nepodařilo se spočítat doklady.')))
  }, [selection, resend])

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      onQueued(await digitooApi.send({ ...selection, resend }))
    } catch (e) {
      setError(errorMessage(e, 'Export se nepodařilo spustit.'))
      setBusy(false)
    }
  }

  const skipped = preview
    ? [
        preview.skippedWithoutFiles > 0 && `${preview.skippedWithoutFiles} pohybů bez dokladu`,
        preview.alreadySent > 0 && `${preview.alreadySent} dokladů už v Digitoo je`,
        preview.alreadyQueued > 0 && `${preview.alreadyQueued} dokladů už čeká ve frontě`,
        preview.tooLarge > 0 && `${preview.tooLarge} dokladů je na e-mail moc velkých (nad 18 MB)`,
      ].filter(Boolean)
    : []

  return (
    <Modal title="Exportovat do Digitoo" onClose={onClose}>
      {!preview && !error && <p className="text-sm text-slate-400">Počítám…</p>}

      {preview && !preview.configured && (
        <div className="space-y-3 text-sm">
          <p>Odesílání do Digitoo ještě není nastavené.</p>
          {isAdmin ? (
            <Link to="/admin/nastaveni" className="text-brand-600 hover:underline">
              Nastavit Gmail a adresu Digitoo →
            </Link>
          ) : (
            <p className="text-slate-500">Požádej admina, ať ho nastaví v Nastavení.</p>
          )}
        </div>
      )}

      {preview && preview.configured && (
        <div className="space-y-4 text-sm">
          {preview.fileCount > 0 ? (
            <p>
              Odešle se <strong>{preview.fileCount}</strong> dokladů ({formatBytes(preview.totalBytes)}) k{' '}
              <strong>{preview.movementCount}</strong> pohybům na <strong>{preview.recipient}</strong>. Každý doklad
              jako samostatný e-mail.
            </p>
          ) : (
            <p className="font-medium">Ve výběru není nic k odeslání.</p>
          )}
          {skipped.length > 0 && (
            <div className="rounded-lg bg-slate-50 p-3 text-slate-600">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">Přeskočí se</div>
              <ul className="list-inside list-disc">
                {skipped.map((s) => (
                  <li key={s as string}>{s}</li>
                ))}
              </ul>
            </div>
          )}
          {(preview.alreadySent > 0 || resend) && (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={resend} onChange={() => setResend(!resend)} />
              Poslat znovu i doklady, které už v Digitoo jsou
            </label>
          )}
        </div>
      )}

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Zrušit
        </Button>
        {preview?.configured && (
          <Button onClick={confirm} disabled={busy || preview.fileCount === 0}>
            {busy ? 'Odesílám…' : `Odeslat ${preview.fileCount} dokladů`}
          </Button>
        )}
      </div>
    </Modal>
  )
}
